/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import {
    MapContainer,
    Marker,
    Polygon,
    Popup,
    TileLayer,
    useMap,
} from 'react-leaflet'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { getCatchmentArea, getPointOfSale } from '../../services/PointOfSale/PointOfSale.service'
import { useSearchParams } from 'react-router-dom-v5-compat'
import { Filters } from './Filters'

import { HeatmapLayer } from './HeatmapLayer'
import { Summary } from './Summary'
import { CanvasLogosLayer } from './CanvasLogosLayer'
import { LegendControl } from './LegendControl'


const pointOfSaleIcon = new L.Icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
})


const progressSlide = keyframes`
    0% {
        left: -40%;
        width: 40%;
    }
    60% {
        left: 60%;
        width: 40%;
    }
    100% {
        left: 100%;
        width: 40%;
    }
`

const FitBounds = ({bounds, padding = [50, 50]}) => {
    const map = useMap()
    const prevBoundsRef = useRef(null)
    if (bounds && bounds !== prevBoundsRef.current) {
        prevBoundsRef.current = bounds
        map.fitBounds(bounds, {padding})
    }
    return null
}

// Generic hook: manages a Set and provides a stable toggle function
const useSetToggle = (initial = () => new Set()) => {
    const [set, setSet] = useState(initial)
    const toggle = useCallback((key) => {
        setSet(prev => {
            const next = new Set(prev)
            next.has(key) ? next.delete(key) : next.add(key)
            return next
        })
    }, [])
    return [set, toggle]
}

const CatchmentArea = () => {
    const [searchParams] = useSearchParams()
    const posId = searchParams.get('merchant')
    const from = searchParams.get('from')
    const to = searchParams.get('to')
    const platformIds = searchParams.getAll('platform')

    const [hiddenPlatformModules, togglePlatformModule] = useSetToggle()
    const [visiblePolygonKeys, togglePolygonKey] = useSetToggle()
    const [hiddenOverlays, toggleOverlay] = useSetToggle(() => new Set(['heatmap']))

    const {data: pointOfSale} = useQuery({
        queryKey: ['pointofsales', posId],
        queryFn: () => getPointOfSale(posId),
        enabled: !!posId,
    })

    const {data, isFetching} = useQuery({
        queryKey: ['pointofsales', posId, 'catchment-area', from, to, platformIds],
        queryFn: async ({ signal }) => getCatchmentArea(posId, {from, to, platformIds}, signal),
        enabled: !!posId,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
    })

    const availablePlatformModules = useMemo(
        () => [...new Set((data?.points ?? []).map(p => p.platformModule))],
        [data?.points],
    )

    const availablePolygons = useMemo(
        () => (data?.polygons ?? []).map(p => ({
            key: p.platformId ?? 'all',
            platformModule: p.platformModule ?? null,
        })),
        [data?.polygons],
    )

    const heatmapPoints = useMemo(
      () => data?.heatmap?.map(({latitude, longitude, intensity}) => [latitude, longitude, intensity]) ?? [],
      [data?.heatmap],
    )

    const bounds = useMemo(() => {
        const latLngs = []
        if (pointOfSale?.location) {
            latLngs.push([pointOfSale.location.latitude, pointOfSale.location.longitude])
        }
        data?.points?.forEach(p => latLngs.push([p.location.latitude, p.location.longitude]))
        if (latLngs.length === 0) return null
        return L.latLngBounds(latLngs).pad(0.1)
    }, [pointOfSale, data])

    const parisLocation = [48.8566, 2.3522]
    const centerLocation = pointOfSale?.location ?
      [pointOfSale.location.latitude, pointOfSale.location.longitude] :
      parisLocation

    return (
      <>
          <Filters/>

          <Summary/>

          <div css={css`
              position: relative;
              isolation: isolate;
          `}
          >
              {isFetching && (
                <div css={css`
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 2px;
                    overflow: hidden;
                    background: transparent;
                    z-index: 1000;

                    &::after {
                        content: '';
                        position: absolute;
                        top: 0;
                        height: 100%;
                        background: grey;
                        animation: ${progressSlide} 1.4s ease-in-out infinite;
                    }
                `}/>
              )}
              <MapContainer
                center={centerLocation}
                zoom={13}
                scrollWheelZoom
                css={css`
                    width: 100%;
                    height: 70vh;
                `}
              >
                  <TileLayer
                      attribution="Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012"
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
                  />

                  {pointOfSale && pointOfSale.location && (
                    <Marker position={[pointOfSale.location.latitude, pointOfSale.location.longitude]}
                            icon={pointOfSaleIcon}>
                        <Popup>
                            {pointOfSale.name}
                        </Popup>
                    </Marker>
                  )}

                  {data?.polygons?.filter(p => visiblePolygonKeys.has(p.platformId ?? 'all')).map(polygon => {
                      const { type, coordinates } = polygon.hull
                      const positions = type === 'MultiPolygon'
                          ? coordinates.map(poly => poly[0].map(([lng, lat]) => [lat, lng]))
                          : coordinates[0].map(([lng, lat]) => [lat, lng])
                      return (
                          <Polygon
                            key={polygon.platformId ?? 'all'}
                            positions={positions}
                            pathOptions={{
                                color: 'teal',
                                fillColor: 'teal',
                                fillOpacity: 0.2,
                                weight: 2,
                            }}
                          />
                      )
                  })}

                  {data?.points && data.points.length > 0 && (
                      <CanvasLogosLayer
                          points={data.points}
                          hiddenPlatformModules={hiddenPlatformModules}
                      />
                  )}

                  {data?.heatmap && data.heatmap.length > 0 && !hiddenOverlays.has('heatmap') && (
                      <HeatmapLayer
                        points={heatmapPoints}
                        radius={25}
                        blur={15}
                        minOpacity={0.4}
                        gradient={{
                            0.4: 'blue',
                            0.6: 'lime',
                            0.7: 'yellow',
                            0.8: 'orange',
                            1.0: 'red',
                        }}
                      />
                  )}

                  <FitBounds bounds={bounds}/>

                  <LegendControl
                      platforms={availablePlatformModules}
                      polygons={availablePolygons}
                      hasHeatmap={!!(data?.heatmap && data.heatmap.length > 0)}
                      hiddenPlatformModules={hiddenPlatformModules}
                      visiblePolygonKeys={visiblePolygonKeys}
                      hiddenOverlays={hiddenOverlays}
                      onTogglePlatform={togglePlatformModule}
                      onTogglePolygon={togglePolygonKey}
                      onToggleOverlay={toggleOverlay}
                  />
              </MapContainer>
          </div>
      </>
    )
}

export { CatchmentArea }
